import { useEffect, useState } from 'react';

/**
 * Light/dark theme, persisted and shared across every screen.
 *
 * The actual data-theme attribute is set on <html> by a tiny script in
 * index.html *before first paint*, so there is no flash of the wrong theme on
 * load. This module keeps React components in step with that attribute and lets
 * any of them toggle it. State lives in one place — the DOM attribute plus
 * localStorage — so two toggles (the marketing nav and the app top bar) never
 * disagree.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'nyumbasuite.theme';

/** The theme the pre-paint script already resolved onto <html>. */
function current(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/** Applies a theme everywhere: the attribute CSS reads, the browser chrome
 *  colour, and the stored preference that survives a reload. */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#101B29' : '#10294B');
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* storage disabled */ }
}

/** Subscribe a component to the current theme and let it flip. */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(current);

  // Keep this component honest if another toggle (or another tab) changes it.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
        setTheme(e.newValue);
        document.documentElement.setAttribute('data-theme', e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  return { theme, toggle };
}
